import {
  StringSearchFilterType,
  useLoadAssignmentDataQuery,
} from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import _ from 'lodash';
import React, { Fragment, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import * as Icon from '../../components/icons';
import LoadingIndicator from '../../components/LoadingIndicator';
import { StatListItem } from '../../components/stats/StatListItem';
import Tooltip from '../../layout/tooltip/ToolTip';
import HtmlTooltipLayer from '../../layout/viewer/layers/HtmlTooltipLayer';
import { viewerHoveredLocation } from '../../layout/viewer/store/viewer.state';
import useActualityHeatmapItems from '../hooks/useActualityHeatmapItems';
import { actualitySelectedDocument } from '../store/actuality.state';

const styleLabel = classNames(
  'flex items-center',
  'p-1 px-2',
  'text-xxs',
  'bg-menu-active',
  'text-menu-active-text',
  'uppercase',
);

const styleValue = classNames(
  'flex',
  'p-1 px-2',
  'text-sm font-bold',
  'text-center',
  'text-menu-text bg-app-panel-dark',
  'items-center',
);

const styleSectionHeader = classNames(
  'flex items-center justify-between',
  'p-1 px-2',
  'text-xs font-semibold',
  'bg-app-panel-dark/30',
  'text-menu-text/80',
);

const AssignedItem = ({ locationId }: { locationId: string }) => {
  const { t } = useTranslation('feed');

  const actuality = useRecoilValue(actualitySelectedDocument);
  const { getLocationItems } = useActualityHeatmapItems();

  // Get event items for comparison
  const eventItems = getLocationItems(locationId);

  const { data, loading, error } = useLoadAssignmentDataQuery({
    variables: {
      assignmentId: actuality.assignmentId,
      filter: {
        locationId: {
          type: StringSearchFilterType.EQ,
          value: locationId,
        },
      },
    },
  });

  if (loading) {
    return (
      <div>
        <LoadingIndicator
          circleSize={20}
          circleStroke={4}
          transparent
          oneLiner
          // message={t`Checking...`}
          className="text-xxs"
        ></LoadingIndicator>
      </div>
    );
  }

  if (error) {
    return <div>{t`Error`}</div>;
  }

  const items = data?.assignment?.items?.content || [];

  // Check if there are any items to display
  if (items.length === 0) {
    return (
      <div>
        <Tooltip label={t`Assigned`} classNameLabel={styleLabel} />
        <div
          className={classNames(
            'p-1 px-2',
            'text-xs',
            'text-menu-text/60',
            'italic',
          )}
        >
          {t`No assignment found`}
        </div>
      </div>
    );
  }

  return (
    <div>
      {_.map(items, a => {
        // Check if this assigned item matches any event item
        const matchingEventItem = eventItems.find(
          eventItem =>
            eventItem.sku === a.sku || eventItem.itemRanked?.sku === a.sku,
        );

        const isMatchingEvent = !!matchingEventItem;

        return (
          <Fragment key={a.sku}>
            <Tooltip
              label={t`Assigned`}
              classNameLabel={styleLabel}
              // className="flex w-full items-center"
              icon={
                isMatchingEvent ? (
                  <Icon.CircleOk className="h-4 w-4 fill-current" />
                ) : (
                  <Icon.TriangleInfo className="h-4 w-4 fill-current" />
                )
              }
            ></Tooltip>
            <StatListItem
              title={a.sku}
              value={a.stockUomQuantity}
              valueStyle={{
                color: isMatchingEvent ? 'green' : 'red',
                borderColor: isMatchingEvent ? 'green' : 'red',
                borderWidth: 1,
              }}
            />
          </Fragment>
        );
      })}
    </div>
  );
};

interface FeedLayoutTooltipProps {
  maxVisibleItems?: number;
  showAllMetrics?: boolean; // New prop to show both events and assignment data
  groupByDataType?: boolean; // New prop to group items by data type
}

const FeedLayoutTooltip: React.FC<FeedLayoutTooltipProps> = ({
  maxVisibleItems = 5,
  showAllMetrics = false,
  groupByDataType = true,
}) => {
  const { t } = useTranslation('feed');
  const location = useRecoilValue(viewerHoveredLocation);
  const { getLocationItems, getAllLocationItems } = useActualityHeatmapItems();

  // Get items based on mode
  const itemsData = showAllMetrics
    ? getAllLocationItems(location?.locationId)
    : {
        allItems: getLocationItems(location?.locationId),
        eventsItems: [],
        assignmentItems: [],
      };

  // console.log('tooltip items data', itemsData);

  // More robust validation - check if items have valid data to display
  const hasValidItems = (items: typeof itemsData.allItems) => {
    return (
      !_.isEmpty(items) &&
      items.some(
        item => item && item.sku && item.heatmapValue && item.itemRanked,
      )
    );
  };

  const hasItems = hasValidItems(itemsData.allItems);
  const hasEventsItems = hasValidItems(itemsData.eventsItems);
  const hasAssignmentItems = hasValidItems(itemsData.assignmentItems);
  // Check if events and assignment items are the same (by skuKey)
  const areItemsSame =
    hasEventsItems &&
    hasAssignmentItems &&
    itemsData.eventsItems.length === 1 &&
    itemsData.assignmentItems.length === 1 &&
    itemsData.eventsItems[0].skuKey === itemsData.assignmentItems[0].skuKey;

  const renderItems = (
    items: typeof itemsData.allItems,
    sectionTitle?: string,
  ) => {
    if (_.isEmpty(items)) return null;

    // Filter out invalid items that don't have required data
    const validItems = items.filter(
      item => item && item.sku && item.heatmapValue && item.itemRanked,
    );

    if (_.isEmpty(validItems)) return null;

    const visibleItems = _.take(validItems, maxVisibleItems);
    const remainingCount = validItems.length - maxVisibleItems;

    return (
      <Fragment>
        {sectionTitle && <Tooltip label={sectionTitle} />}

        {_.map(visibleItems, item => (
          <Fragment
            key={`${item.consignee}-${item.sku}-${item.dataType}-${location.locationId}`}
          >
            <Tooltip
              classNameLabel={styleLabel}
              // classNameValue={styleValue}
              // value={item.itemRanked?.sku || item.sku}
            />
            <StatListItem
              title={item.itemRanked?.sku || item.sku}
              value={item.heatmapValue}
              valueStyle={{
                color: item.bucket?.color,
                borderColor: item.bucket?.color,
                borderWidth: item.bucket?.color ? 1 : 0,
              }}
            />
          </Fragment>
        ))}

        {/* Show "and X more" message if there are more than maxVisibleItems */}
        {remainingCount > 0 && (
          <div
            className={classNames(
              'min-w-20',
              'p-1 px-2',
              'text-xs',
              'bg-app-panel-dark/70',
              'backdrop-saturate-110 backdrop-blur-lg backdrop-filter',
            )}
          >
            {t(`and {{itemCount}} more`, {
              itemCount: remainingCount,
            })}
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <HtmlTooltipLayer>
      <Suspense>
        {!_.isEmpty(location) && (
          <div className="bg-app-panel-dark">
            {hasItems && (
              <>
                {showAllMetrics && groupByDataType ? (
                  // Grouped view showing events and assignments separately
                  <Fragment>
                    {hasEventsItems && hasAssignmentItems && areItemsSame ? (
                      // Same item in both events and assignment - show as single item
                      renderItems(itemsData.eventsItems, t`Item`)
                    ) : (
                      // Different items or only one type exists - show separately
                      <Fragment>
                        {hasEventsItems &&
                          renderItems(itemsData.eventsItems, t`Item`)}

                        {hasAssignmentItems &&
                          renderItems(
                            itemsData.assignmentItems,
                            t`Last Assigned Item`,
                          )}
                      </Fragment>
                    )}
                  </Fragment>
                ) : showAllMetrics ? (
                  // Combined view showing all items together
                  renderItems(
                    itemsData.allItems,
                    hasEventsItems && hasAssignmentItems
                      ? t`All Metrics`
                      : undefined,
                  )
                ) : (
                  // Original single-type view
                  <Fragment>
                    <Tooltip label={t`Item`} />
                    {renderItems(itemsData.allItems)}
                  </Fragment>
                )}
              </>
            )}
            <Suspense>
              {(() => {
                return <AssignedItem locationId={location?.locationId} />;
              })()}
            </Suspense>
          </div>
        )}
      </Suspense>
    </HtmlTooltipLayer>
  );
};

export default FeedLayoutTooltip;
